class VideoHeroStickyCta extends HTMLElement {
  static VISIBLE_CLASS = 'is-visible';
  static DESKTOP_MIN_WIDTH = 1025;
  // Same offsets the product sticky bar uses: full header, collapsed header, no header.
  static TOP_OFFSET = { pinned: '113px', collapsed: '48px', hidden: '0px' };
  static DEFAULTS = {
    trigger: '[data-video-hero-cta]',
    fallbackTrigger: '.video-from-cdn',
    footer: '.footer',
    header: '.header__wrap'
  };

  constructor() {
    super();

    this.desktopQuery = window.matchMedia(
      `(min-width: ${VideoHeroStickyCta.DESKTOP_MIN_WIDTH}px)`
    );

    this.updateFooterOverlap = this.updateFooterOverlap.bind(this);
    this.updateTopOffset = this.updateTopOffset.bind(this);
    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    const { trigger, fallbackTrigger, footer, header } =
      VideoHeroStickyCta.DEFAULTS;
    const scope = this.closest('.shopify-section') || document;

    this.trigger =
      scope.querySelector(this.dataset.trigger || trigger) ||
      scope.querySelector(fallbackTrigger);
    this.footer = document.querySelector(this.dataset.footer || footer);
    this.header = document.querySelector(header);

    this.triggerVisible = true;
    this.footerOverlap = false;

    this.setupTriggerObserver();
    this.setupScrollListener();
    this.setupHeaderObserver();

    this.updateTopOffset();
    this.updateVisibility();
  }

  disconnectedCallback() {
    this.triggerObserver?.disconnect();
    this.headerObserver?.disconnect();
    document.removeEventListener('scroll', this.updateFooterOverlap);
    window.removeEventListener('resize', this.onResize);
    this.initialized = false;
  }

  get isDesktop() {
    return this.desktopQuery.matches;
  }

  setupTriggerObserver() {
    if (!this.trigger) return;

    this.triggerObserver = new IntersectionObserver(
      ([entry]) => {
        this.triggerVisible = entry.isIntersecting;
        this.updateVisibility();
      },
      { threshold: 0 }
    );

    this.triggerObserver.observe(this.trigger);
  }

  setupScrollListener() {
    this.updateFooterOverlap();
    document.addEventListener('scroll', this.updateFooterOverlap, {
      passive: true
    });
    window.addEventListener('resize', this.onResize, { passive: true });
  }

  setupHeaderObserver() {
    if (!this.header) return;

    this.headerObserver = new MutationObserver(this.updateTopOffset);
    this.headerObserver.observe(this.header, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  onResize() {
    this.updateTopOffset();
    this.updateFooterOverlap();
  }

  updateTopOffset() {
    const { TOP_OFFSET } = VideoHeroStickyCta;
    let offset = TOP_OFFSET.pinned;

    if (document.body.classList.contains('hide-header')) {
      offset = TOP_OFFSET.hidden;
    } else if (this.header?.classList.contains('header-up')) {
      offset = TOP_OFFSET.collapsed;
    }

    this.style.setProperty('--video-hero-sticky-top', offset);
  }

  updateFooterOverlap() {
    const overlap = this.isDesktop ? false : this.overlapsFooter();

    if (this.footerOverlap === overlap) return;

    this.footerOverlap = overlap;
    this.updateVisibility();
  }

  overlapsFooter() {
    if (!this.footer) return false;

    const barBottom = window.innerHeight;
    const barTop = barBottom - this.offsetHeight;
    const footerRect = this.footer.getBoundingClientRect();

    return barBottom > footerRect.top && barTop < footerRect.bottom;
  }

  updateVisibility() {
    const shouldShow = !this.triggerVisible && !this.footerOverlap;
    this.classList.toggle(VideoHeroStickyCta.VISIBLE_CLASS, shouldShow);
  }
}

if (!customElements.get('video-hero-sticky-cta')) {
  customElements.define('video-hero-sticky-cta', VideoHeroStickyCta);
}
